import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Landmark, Pencil, CheckCircle2, Clock, Phone, Printer, Mail, Globe, Send, Clock3, MapPin, ChevronDown, ChevronRight, RotateCcw, Eye, XCircle, AlertCircle, FileText, Download, Trash2, UploadCloud, Image as ImageIcon, FileAudio } from 'lucide-react';
import { InlineAudioPlayer, isAudioAttachment } from './InlineAudioPlayer';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { JURISDICTION_TYPE_OPTIONS, getJurisdictionTypeLabel } from '../../lib/jurisdictionTaxonomy';
import { formatDateOnlyET, displayTitleCase, displayUSAddress } from '../../utils/formatters';
import { RequestJurisdictionCorrectionDialog, CORRECTION_ID, FIELD_LABEL, showCorrectionValue } from './RequestJurisdictionCorrectionDialog';
import { MlcCollapse } from './MlcCollapse';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const DOC_MAX_BYTES = 10 * 1024 * 1024;
const AUDIO_MAX_BYTES = 25 * 1024 * 1024;
const AUDIO_EXT = /\.(mp3|wav|m4a|ogg)$/i;
const isDocFile = (f) => ['application/pdf', 'image/jpeg', 'image/png'].includes(f.type);
const isAudioFile = (f) => AUDIO_EXT.test(f.name || '') && (!f.type || /^audio\//.test(f.type) || f.type === 'application/ogg' || f.type === 'application/octet-stream');
const COUNTY_TYPES = ['county', 'parish'];
export const CAN_REQUEST_ROLES = ['tax_processor', 'processor', 'tax_search_team_leader', 'admin', 'jurisdiction_manager'];
// PJR-002-B — identical to the Jurisdiction Directory (Mail / Fax / Email only).
export const INFO_OBTAINED_BACK_OPTIONS = [['mail', 'Mail'], ['fax', 'Fax'], ['email', 'Email']];

// PJR-001-B — optional collector / payment / address / website fields (same keys as the Jurisdiction Directory).
export const COLLECTOR_FIELDS = [
  'tax_collector_name', 'make_checks_payable_to', 'tax_collector_phone', 'tax_collector_phone_ext', 'tax_collector_email', 'tax_collector_fax',
  'tax_collector_working_hours', 'tax_collector_physical_address', 'tax_collector_mailing_address', 'tax_collector_website', 'assessor_website',
];
const PHONE_FIELDS = ['tax_collector_phone', 'tax_collector_fax'];
const TITLE_FIELDS = ['tax_collector_name', 'make_checks_payable_to'];
const digits = (v) => (v || '').replace(/\D/g, '');
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const URL_RE = /^(https?:\/\/)?[^\s/$.?#][^\s]*\.[^\s]{2,}$/i;
// Mandatory at submission (PJR-001-B): payee, phone, physical address. Collector name stays mandatory before approval.
export const REQUIRED_AT_SUBMIT = {
  make_checks_payable_to: 'Make Checks Payable To is required.',
  tax_collector_phone: 'Phone Number is required (10-digit US number).',
  tax_collector_physical_address: 'Physical Address is required.',
};
export const validateCollectorField = (k, v, { requireMandatory = false } = {}) => {
  const s = (v || '').trim();
  if (!s) return requireMandatory && REQUIRED_AT_SUBMIT[k] ? REQUIRED_AT_SUBMIT[k] : '';
  if (PHONE_FIELDS.includes(k)) return digits(s).length === 10 ? '' : 'Enter a 10-digit US number.';
  if (k === 'tax_collector_email') return EMAIL_RE.test(s) ? '' : 'Enter a valid email address.';
  if (k === 'tax_collector_website' || k === 'assessor_website') return URL_RE.test(s) ? '' : 'Enter a valid website (https://…).';
  return '';
};

/** 302-291-2654 while typing; accepts pasted "(302) 291 2654", "302.291.2654", "+1 302…" (leading 1 dropped). */
export const formatUsPhone = (v) => {
  let d = digits(v);
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};

const SMALL_WORDS = new Set(['of', 'and', 'the', 'for', 'at', 'by', 'in', 'on', 'to', 'a', 'an', 'de', 'la', 'del']);
const caseWord = (w, first) => {
  if (!w) return w;
  if (/[\d.&/@]/.test(w)) return w;                                  // St., A&M, 1st, P.O.
  if (/^[A-Z]{2,5}$/.test(w)) return w;                               // LLC, PA, CDD, ISD, MUD
  if (/[a-z]/.test(w) && /[A-Z]/.test(w.slice(1))) return w;         // McDonald, DeSoto — already mixed case
  const lower = w.toLowerCase();
  if (!first && SMALL_WORDS.has(lower)) return lower;
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};
/** "nithish raju" → "Nithish Raju"; keeps acronyms / abbreviations / official mixed-case names. */
export const smartTitleCase = (s) => {
  let first = true;
  return (s || '').replace(/[^\s]+/g, (tok) => {
    const out = tok.split('-').map((part, i) => caseWord(part, first && i === 0)).join('-');
    first = false;
    return out;
  });
};

// One requests read per order per page load (all statuses — shared by every mount for the order).
const reqCache = new Map();
const loadRequests = (orderId, force = false) => {
  if (force || !reqCache.has(orderId)) {
    reqCache.set(orderId, axios.get(`${API}/api/jurisdiction-requests`, { params: { order_id: orderId, limit: 50 }, ...auth() })
      .then((r) => r.data?.items || []).catch(() => []));
  }
  return reqCache.get(orderId);
};
const sameIdentity = (a, b) => a && b && (a.state || '').toUpperCase() === (b.state || '').toUpperCase()
  && (a.jurisdiction_type || '').toLowerCase() === (b.jurisdiction_type || '').toLowerCase()
  && (a.jurisdiction_name || '').trim().toUpperCase() === (b.jurisdiction_name || '').trim().toUpperCase()
  && (['county', 'parish'].includes((a.jurisdiction_type || '').toLowerCase()) || !a.parent_county_name || !b.parent_county_name
      || (a.parent_county_name || '').replace(/\s+(county|parish)$/i, '').trim().toUpperCase() === (b.parent_county_name || '').replace(/\s+(county|parish)$/i, '').trim().toUpperCase());
// Newest request per jurisdiction identity (a resubmission supersedes the rejected one it revises).
const latestPerIdentity = (items) => {
  const out = [];
  const superseded = new Set(items.map((r) => r.revision_of_id).filter(Boolean));
  [...items].filter((r) => !superseded.has(r.id)).sort((a, b) => (b.submitted_at || '').localeCompare(a.submitted_at || '')).forEach((r) => {
    if (!out.some((o) => sameIdentity(o.payload, r.payload))) out.push(r);
  });
  return out;
};

const STATUS_PILL = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
};
const fmtDate = (iso) => (iso ? formatDateOnlyET(iso) : '—');

/** Read-only request summary (processor-safe: no reviewer identities, no editing). */
export const RequestDetailsLiteDialog = ({ request, onClose, onCorrect }) => {
  if (!request) return null;
  const p = request.payload || {};
  const isRejected = request.status === 'rejected';
  const rows = [
    ['Status', <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${STATUS_PILL[request.status] || ''}`} data-testid="rdl-status">{request.status}</span>],
    ['Jurisdiction', `${displayTitleCase(p.jurisdiction_name)}, ${p.state} · ${getJurisdictionTypeLabel(p.jurisdiction_type)}`],
    ...(p.parent_county_name ? [['Parent County', displayTitleCase(p.parent_county_name)]] : []),
    ['Request ID', `JR-${(request.id || '').slice(0, 8).toUpperCase()}`],
    ['Submitted', `${fmtDate(request.submitted_at)}${request.submitted_by_name ? ` · ${request.submitted_by_name}` : ''}`],
    ...(request.order_number ? [['Order', request.order_number]] : []),
    ['Collector details', `${(request.collector_fields_provided || COLLECTOR_FIELDS.filter((k) => p[k])).length}/${COLLECTOR_FIELDS.length} provided`],
    ...(p.make_checks_payable_to ? [['Payee', displayTitleCase(p.make_checks_payable_to)]] : []),
    ...(p.tax_collector_phone ? [['Phone', `${formatUsPhone(p.tax_collector_phone)}${p.tax_collector_phone_ext ? ` ext. ${p.tax_collector_phone_ext}` : ''}`]] : []),
    ...(p.tax_collector_physical_address ? [['Physical Address', displayUSAddress(p.tax_collector_physical_address)]] : []),
    ...(request.status === 'rejected' && request.rejection_reason ? [['Rejection reason', <span className="text-rose-700">{request.rejection_reason}</span>]] : []),
    ...(request.status === 'approved' ? [['Directory', 'Approved — refresh the jurisdiction list and select it']] : []),
  ];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[520px] p-0 gap-0 rounded-[20px] overflow-hidden z-[121] border-0 shadow-2xl shadow-indigo-900/20" data-testid="rdl-dialog">
        <DialogHeader className={`px-6 pt-5 pb-4 text-left bg-gradient-to-r ${isRejected ? 'from-rose-50 via-white to-slate-50' : 'from-indigo-50 via-white to-violet-50'}`}>
          <div className="flex items-center gap-3">
            <span className={`h-10 w-10 rounded-xl grid place-items-center ${isRejected ? 'bg-rose-100 text-rose-600' : 'bg-indigo-100 text-indigo-600'}`}>{isRejected ? <XCircle className="h-5 w-5" /> : <Landmark className="h-5 w-5" />}</span>
            <div>
              <DialogTitle className="text-[17px] font-bold text-slate-900">{isRejected ? 'Request Rejected' : 'Jurisdiction Request'}</DialogTitle>
              <DialogDescription className="text-[13px] text-slate-500">{isRejected ? 'Correct the information and resubmit for review.' : 'Read-only summary of the existing request.'}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        {isRejected && (
          <div className="mx-6 mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3" data-testid="rdl-rejection-banner">
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-rose-700">Rejection reason</p>
            <p className="mt-0.5 text-[13px] font-medium text-rose-900" data-testid="rdl-rejection-reason">{request.rejection_reason || 'No reason provided'}</p>
            <p className="mt-1 text-[12px] text-rose-700/80">Your previously entered details are kept — only the fields that need correction have to change.</p>
          </div>
        )}
        <div className="px-6 py-4 space-y-2 text-[13px]">
          {rows.map(([l, v]) => <div key={l} className="flex justify-between gap-4"><span className="text-slate-500">{l}</span><span className="font-medium text-slate-800 text-right">{v}</span></div>)}
        </div>
        <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="h-9 rounded-xl" data-testid="rdl-close">Close</Button>
          {isRejected && onCorrect && (
            <Button onClick={() => onCorrect(request)} className="h-9 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white" data-testid="rdl-correct-resubmit-btn">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Correct &amp; Resubmit
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** JCR-001 — read-only correction summary for the processor (old → new per field, reason, attachments). */
export const CorrectionDetailsLiteDialog = ({ request, onClose, onCorrect }) => {
  if (!request) return null;
  const p = request.payload || {};
  const isRejected = request.status === 'rejected';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[560px] p-0 gap-0 rounded-[20px] overflow-hidden z-[121] border-0 shadow-2xl shadow-indigo-900/20" data-testid="jcr-lite-dialog">
        <DialogHeader className={`px-6 pt-5 pb-4 text-left bg-gradient-to-r ${isRejected ? 'from-rose-50 via-white to-slate-50' : 'from-indigo-50 via-white to-violet-50'}`}>
          <div className="flex items-center gap-3">
            <span className={`h-10 w-10 rounded-xl grid place-items-center ${isRejected ? 'bg-rose-100 text-rose-600' : 'bg-indigo-100 text-indigo-600'}`}>{isRejected ? <XCircle className="h-5 w-5" /> : <Pencil className="h-5 w-5" />}</span>
            <div>
              <DialogTitle className="text-[17px] font-bold text-slate-900">{isRejected ? 'Correction Rejected' : 'Correction Request'}</DialogTitle>
              <DialogDescription className="text-[13px] text-slate-500">{CORRECTION_ID(request.id)} · {displayTitleCase(p.jurisdiction_name)}, {p.state} · <span className="capitalize">{request.status === 'approved' ? 'Applied to directory' : request.status}</span></DialogDescription>
            </div>
          </div>
        </DialogHeader>
        {isRejected && <div className="mx-6 mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px]" data-testid="jcr-lite-rejection"><b className="text-rose-800">Rejection reason:</b> <span className="text-rose-900" data-testid="jcr-lite-rejection-reason">{request.rejection_reason || 'No reason provided'}</span></div>}
        <div className="px-6 py-4 space-y-2 text-[13px]">
          {(request.changes || []).map((c) => (
            <div key={c.field} className="grid grid-cols-[150px_1fr_16px_1fr] items-center gap-2" data-testid={`jcr-lite-change-${c.field}`}>
              <span className="text-slate-500">{FIELD_LABEL[c.field] || c.field}</span>
              <span className="rounded-md bg-rose-50 text-rose-700 px-2 py-1 truncate" title={showCorrectionValue(c.field, c.current_value)}>{showCorrectionValue(c.field, c.current_value)}</span>
              <span className="text-slate-400 text-center">→</span>
              <span className="rounded-md bg-emerald-50 text-emerald-700 px-2 py-1 font-medium truncate" title={showCorrectionValue(c.field, c.proposed_value)}>{showCorrectionValue(c.field, c.proposed_value)}</span>
            </div>
          ))}
          <div className="pt-2 border-t border-slate-100"><span className="text-slate-500">Reason:</span> <span className="text-slate-800">{request.reason}</span></div>
          {request.attachments?.length > 0 && <div className="flex flex-wrap gap-2">{request.attachments.map((a) => <a key={a.file_id} href={`${API}/api/jurisdiction-correction-requests/${request.id}/attachments/${a.file_id}`} target="_blank" rel="noreferrer" onClick={async (e) => { e.preventDefault(); try { const r = await axios.get(`${API}/api/jurisdiction-correction-requests/${request.id}/attachments/${a.file_id}`, { ...auth(), responseType: 'blob' }); window.open(URL.createObjectURL(r.data), '_blank'); } catch { toast.error('Attachment unavailable'); } }} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[12px] text-indigo-700 hover:bg-indigo-50" data-testid={`jcr-lite-att-${a.file_id.slice(0, 8)}`}>{a.filename} <span className="text-slate-400">{Math.round((a.size || 0) / 1024)} KB</span></a>)}</div>}
        </div>
        <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="h-9 rounded-xl" data-testid="jcr-lite-close">Close</Button>
          {isRejected && onCorrect && <Button onClick={() => onCorrect(request)} className="h-9 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white" data-testid="jcr-lite-correct-btn"><RotateCcw className="h-4 w-4 mr-1.5" /> Correct &amp; Resubmit</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const REQ_ACCENT = {
  pending: { bar: 'bg-amber-400', pill: 'bg-amber-50 text-amber-800 ring-amber-200', icon: Clock, label: 'Pending' },
  approved: { bar: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200', icon: CheckCircle2, label: 'Available' },
  rejected: { bar: 'bg-rose-500', pill: 'bg-rose-50 text-rose-700 ring-rose-200', icon: XCircle, label: 'Rejected' },
};

/** TAX-SIDEBAR-PREMIUM-001 (Oct 2026) — compact white card, left status accent, truncated name + tooltip. */
const RequestStatusCard = ({ r, onView, onRequestAgain, testId }) => {
  const p = r.payload || {};
  const isCorrection = r.request_type === 'jurisdiction_correction';
  const a = REQ_ACCENT[r.status] || REQ_ACCENT.pending;
  const StatusIcon = isCorrection ? Pencil : a.icon;
  const name = displayTitleCase(p.jurisdiction_name);
  const parent = p.parent_county_name ? `${displayTitleCase(p.parent_county_name)}${/county$/i.test(p.parent_county_name) ? '' : ' County'}` : '';
  const secondary = isCorrection
    ? `Correction · ${(r.changes || []).length} field${(r.changes || []).length !== 1 ? 's' : ''} · ${CORRECTION_ID(r.id)}`
    : [getJurisdictionTypeLabel(p.jurisdiction_type || 'county'), parent].filter(Boolean).join(' · ') + `, ${p.state}`;
  const pill = isCorrection ? (r.status === 'approved' ? 'Applied' : a.label) : a.label;
  return (
    <div className="tcp-req-card relative overflow-hidden rounded-xl border border-slate-200 bg-white pl-3.5 pr-2.5 py-2 text-[12px]" data-testid={`${testId}-${isCorrection ? 'correction-' : ''}card-${r.status}`}>
      <span aria-hidden className={`absolute left-0 top-0 bottom-0 w-[3px] ${a.bar}`} />
      <div className="flex items-start gap-2">
        <StatusIcon className={`h-4 w-4 shrink-0 mt-px ${r.status === 'approved' ? 'text-emerald-600' : r.status === 'rejected' ? 'text-rose-500' : 'text-amber-500'}`} />
        <div className="min-w-0 flex-1">
          <div className="tcp-req-head flex items-center gap-1.5">
            <p className="font-semibold text-[13px] text-slate-900 truncate min-w-0" title={name} data-testid={`${testId}-name-${r.id.slice(0, 8)}`}>{name}</p>
            <span className={`tcp-req-pill ml-auto shrink-0 inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10.5px] font-semibold ring-1 ring-inset ${a.pill}`} data-testid={`${testId}-status-${r.id.slice(0, 8)}`}>
              {pill}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 truncate" title={secondary}>{secondary}</p>
        </div>
      </div>
      {r.status === 'rejected' && r.rejection_reason && <p className="mt-1 pl-6 text-[11px] text-rose-700 line-clamp-2" title={r.rejection_reason}>{r.rejection_reason}</p>}
      <div className="mt-1.5 pl-6 flex items-center gap-1">
        <button type="button" onClick={() => onView(r)} className="tcp-req-link flex-1 min-w-0 inline-flex items-center gap-1.5 rounded-md py-0.5 text-[11.5px] font-semibold text-indigo-700 hover:text-indigo-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300" data-testid={`${testId}-view-${r.id.slice(0, 8)}`}>
          <Eye className="h-3.5 w-3.5 shrink-0" /><span className="truncate">View request details</span><ChevronRight className="tcp-chev ml-auto h-3.5 w-3.5 shrink-0 text-slate-400" />
        </button>
      </div>
      {r.status === 'rejected' ? (
        <div className="pl-6 mt-1 flex items-center gap-2 flex-wrap">
          {r.status === 'rejected' && <button type="button" onClick={() => onRequestAgain(r)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 rounded" data-testid={`${testId}-again-${r.id.slice(0, 8)}`}><RotateCcw className="h-3 w-3" /> {isCorrection ? 'Correct & resubmit' : 'Request again'}</button>}
        </div>
      ) : null}
    </div>
  );
};

/** Single primary action + compact status cards (pending / approved / rejected) for requests filed from this order. */
export const RequestMissingJurisdictionAction = ({ orderData, defaultType, defaultName, parentCountyName, onRefreshList, testId = 'request-missing-jurisdiction', variant = 'inline', corrections: correctionsProp = null }) => {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [view, setView] = useState(null);
  const [items, setItems] = useState([]);
  const orderId = orderData?.id;
  const type = (defaultType || 'county').toLowerCase();

  // PJR-001 (Oct 2026) — status freshness. Always fetch on mount (the module cache is only reused for
  // same-tick renders), re-fetch when the tab regains focus (throttled) and when details are opened.
  // Merge rule: an older in-flight "pending" response can never overwrite a newer rejected/approved/
  // resubmitted doc — the doc with the latest `reviewed_at`/`submitted_at` wins per id.
  const lastFetchRef = useRef(0);
  const mergeNewest = (prev, next) => {
    const stamp = (r) => r.reviewed_at || r.resubmitted_at || r.submitted_at || '';
    const byId = new Map(prev.map((r) => [r.id, r]));
    for (const r of next) {
      const cur = byId.get(r.id);
      if (!cur || stamp(r) >= stamp(cur)) byId.set(r.id, r);
    }
    return [...byId.values()].sort((a, b) => (b.submitted_at || '').localeCompare(a.submitted_at || ''));
  };
  const refresh = (force = true) => {
    if (!orderId) return Promise.resolve();
    lastFetchRef.current = Date.now();
    return loadRequests(orderId, force).then((list) => setItems((prev) => mergeNewest(prev, list)));
  };
  // JCR-001 — correction requests for this order (flag-gated endpoint; 404 when off → silently none).
  // JCR-HISTORY-001 — when the parent supplies `corrections` (requests not matching any jurisdiction card), skip the own read.
  const [ownCorrections, setOwnCorrections] = useState([]);
  const corrections = correctionsProp ?? ownCorrections;
  const [corrOpen, setCorrOpen] = useState(null);        // { jurisdictionId, revisionOf }
  const loadCorrections = () => correctionsProp == null && orderId && axios.get(`${API}/api/jurisdiction-correction-requests`, { params: { order_id: orderId }, ...auth() })
    .then((r) => setOwnCorrections(r.data?.items || [])).catch(() => {});
  useEffect(() => { refresh(true); loadCorrections(); }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // FULFILLMENT-JURISDICTION-AUTO-REFRESH: one throttled re-read of requests AND corrections on focus return.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastFetchRef.current > 15000) { refresh(true); loadCorrections(); }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => { document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', onVisible); };
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // FULFILLMENT-JURISDICTION-AUTO-REFRESH: when a request is seen moving to Approved, re-run the existing
  // dropdown lookup once (same callback the manual "Refresh list" used) so the new record is selectable.
  const onRefreshListRef = useRef(onRefreshList);
  onRefreshListRef.current = onRefreshList;
  const seenStatusRef = useRef(new Map());
  useEffect(() => {
    let fire = false;
    for (const r of items) {
      if (r.request_type === 'jurisdiction_correction') continue;
      const prev = seenStatusRef.current.get(r.id);
      if (prev !== undefined && prev !== 'approved' && r.status === 'approved') fire = true;
      seenStatusRef.current.set(r.id, r.status);
    }
    if (fire && onRefreshListRef.current) onRefreshListRef.current();
  }, [items]);

  const cards = latestPerIdentity(items.filter((r) => r.request_type !== 'jurisdiction_correction'));  // JCR-001 rows render below
  const corrSuperseded = new Set(corrections.map((c) => c.revision_of_id).filter(Boolean));
  const corrCards = corrections.filter((c) => !corrSuperseded.has(c.id));
  const openForm = (pre = null) => { setPrefill(pre); setOpen(true); };
  const openView = (r) => {
    setView(r);
    // Re-read so the popup never shows a stale status; keep the popup bound to the freshest doc.
    refresh(true).then(() => setItems((cur) => { const fresh = cur.find((x) => x.id === r.id); if (fresh) setView(fresh); return cur; }));
  };
  const dialog = open && (
    <RequestMissingJurisdictionDialog
      open={open}
      onClose={() => { setOpen(false); setPrefill(null); }}
      orderData={orderData}
      defaultType={prefill?.payload?.jurisdiction_type || type}
      defaultName={prefill ? prefill.payload?.jurisdiction_name : defaultName}
      parentCountyName={parentCountyName}
      prefill={prefill?.payload || null}
      revisionOfId={prefill?.id || null}
      revisionOf={prefill}
      existingRequests={items}
      onViewRequest={(r) => openView(r)}
      onRefreshList={onRefreshList}
      onSubmitted={(req) => { reqCache.set(orderId, Promise.resolve([req, ...items])); setItems((p) => mergeNewest(p, [req])); }}
    />
  );
  const details = view && (view.request_type === 'jurisdiction_correction'
    ? <CorrectionDetailsLiteDialog request={view} onClose={() => setView(null)} onCorrect={(rej) => { setView(null); setCorrOpen({ jurisdictionId: rej.jurisdiction_id, revisionOf: rej }); }} />
    : <RequestDetailsLiteDialog request={view} onClose={() => setView(null)} onCorrect={(rej) => { setView(null); openForm(rej); }} />);
  const corrDialog = corrOpen && (
    <RequestJurisdictionCorrectionDialog open onClose={() => setCorrOpen(null)} orderData={orderData} jurisdictionId={corrOpen.jurisdictionId} revisionOf={corrOpen.revisionOf}
      onSubmitted={() => loadCorrections()} />
  );
  const cardEls = [
    ...cards.map((r) => (
      <RequestStatusCard key={r.id} r={r} testId={testId} onView={openView}
        onRequestAgain={(rej) => openForm(rej)} />
    )),
  ];
  // JCR-HISTORY-001 — corrections stay grouped apart from missing-jurisdiction requests
  if (corrCards.length) {
    cardEls.push(
      <div key="jcr-other" className="space-y-1.5" data-testid={`${testId}-other-corrections`}>
        <p className="px-1 pt-1 text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">Other correction requests</p>
        {corrCards.map((c) => (
          <RequestStatusCard key={c.id} r={c} testId={testId} onView={(r) => setView(r)} onRequestAgain={(rej) => setCorrOpen({ jurisdictionId: rej.jurisdiction_id, revisionOf: rej })} />
        ))}
      </div>
    );
  }

  if (variant === 'rail') {
    return (
      <div className="mt-2 space-y-2" data-testid={`${testId}-rail`}>
        <button type="button"
          className="tcp-rail-action w-full h-12 py-1.5 flex items-center gap-2.5 px-3 rounded-xl border border-indigo-300 bg-indigo-50/40 text-indigo-700 text-[13px] font-semibold leading-tight hover:bg-indigo-50 hover:border-indigo-400"
          onClick={() => openForm()} data-testid={`${testId}-btn`}>
          <Landmark className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-left line-clamp-2">Request Missing Jurisdiction</span>
          <ChevronRight className="tcp-chev h-4 w-4 shrink-0 text-indigo-400" />
        </button>
        {cardEls.length > 0 && <div className="space-y-1.5 pt-1">{cardEls}</div>}
        {dialog}{details}{corrDialog}
      </div>
    );
  }
  return (
    <>
      <Button type="button" size="sm" variant="outline" className="h-8 px-3 text-xs rounded-lg border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800" onClick={() => openForm()} data-testid={`${testId}-btn`}>
        <Landmark className="h-3.5 w-3.5 mr-1.5" /> Request Missing Jurisdiction
      </Button>
      {cardEls.length > 0 && <div className="mt-2 space-y-1.5">{cardEls}</div>}
      {dialog}{details}{corrDialog}
    </>
  );
};

/* ---------- premium form primitives ---------- */
const INPUT = 'h-11 rounded-[10px] border-slate-200 bg-white text-[14px] text-slate-800 placeholder:text-slate-400 shadow-none transition-[box-shadow,border-color] duration-150 hover:border-slate-300 focus-visible:border-indigo-400 focus-visible:ring-4 focus-visible:ring-indigo-500/10';
const Label = ({ children, required }) => (
  <label className="block text-[11px] font-bold uppercase tracking-[0.06em] text-[#1e3a8a] mb-1.5">{children}{required && <span className="text-rose-500"> *</span>}</label>
);
const Section = ({ n, title, testId, children }) => (
  <section className="rmj-card rounded-2xl bg-white border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5" data-testid={testId}>
    <header className="flex items-center gap-3 mb-4">
      <span className="h-7 w-7 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white text-[12px] font-bold grid place-items-center shadow-sm shadow-indigo-500/30">{n}</span>
      <h3 className="text-[15px] font-semibold text-slate-900">{title}</h3>
    </header>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">{children}</div>
  </section>
);
const Field = ({ k, label, value, onChange, onBlur, error, placeholder, icon: Icon, className = '', inputMode, maxLength = 200, required, readOnly }) => (
  <div className={className}>
    <Label required={required}>{label}</Label>
    <div className="relative">
      {Icon && <Icon className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-indigo-500" />}
      <Input value={value} onChange={(e) => onChange?.(k, e.target.value)} onBlur={onBlur} placeholder={placeholder} maxLength={maxLength} inputMode={inputMode} readOnly={readOnly}
        className={`${INPUT} ${Icon ? 'pl-10' : ''} ${readOnly ? 'bg-slate-50 text-slate-600' : ''} ${error ? 'border-rose-300 focus-visible:ring-rose-500/10' : ''}`} data-testid={`rmj-${k.replace(/_/g, '-')}`} />
    </div>
    {error && <p className="text-[11px] text-rose-600 mt-1" data-testid={`rmj-error-${k}`}>{error}</p>}
  </div>
);

export const RequestMissingJurisdictionDialog = ({ open, onClose, orderData, defaultType = 'county', defaultName = '', parentCountyName = '', prefill = null, revisionOfId = null, revisionOf = null, onViewRequest, onRefreshList, onSubmitted }) => {
  const state = (orderData?.property_state || '').toUpperCase();
  const [jType, setJType] = useState(defaultType);
  const [name, setName] = useState(smartTitleCase(defaultName || ''));
  const [col, setCol] = useState(() => Object.fromEntries(COLLECTOR_FIELDS.map((k) => {
    const v = prefill?.[k] || '';
    return [k, PHONE_FIELDS.includes(k) ? formatUsPhone(v) : v];
  })));
  // PJR-001-C — live duplicate / existence check (identity: state + type + normalized name + parent county)
  const [dup, setDup] = useState({ status: 'none', request: null, jurisdiction: null });
  const dupSeq = useRef(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [serverError, setServerError] = useState('');
  const [touched, setTouched] = useState(false);
  // PJR-002: MLC & Workflow + supporting documents (draft-backed)
  const [wf, setWf] = useState({ mlc_required: false, mlc_charges: '', mlc_24_month_charges: '', mlc_eta_days: '', info_obtained_back: '', sase_required: null });
  const setW = (k, v) => setWf((w) => ({ ...w, [k]: v }));
  const [draftId, setDraftId] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef(null);
  useEffect(() => {
    if (!open || !orderData?.id || draftId) return;
    axios.post(`${API}/api/jurisdiction-requests/drafts`, { order_id: orderData.id }, auth())
      .then(({ data }) => { setDraftId(data.draft.id); setFiles(data.draft.attachments || []); })
      .catch(() => setServerError('Could not start a request draft — please retry.'));
  }, [open, orderData?.id, draftId]);
  const uploadFiles = async (list) => {
    const picked = Array.from(list || []).filter(Boolean);
    if (!picked.length || !draftId) return;
    // PJR-AUDIO-UPLOAD-001: documents (PDF/JPG/PNG, 10 MB) + call recordings (MP3/WAV/M4A/OGG, 25 MB)
    const bad = picked.find((f) => !isDocFile(f) && !isAudioFile(f));
    if (bad) { toast.error(`'${bad.name}': only PDF, JPG, PNG, MP3, WAV, M4A and OGG files are accepted`); return; }
    const tooBig = picked.find((f) => (isAudioFile(f) ? f.size > AUDIO_MAX_BYTES : f.size > DOC_MAX_BYTES));
    if (tooBig) { toast.error(`'${tooBig.name}' exceeds the ${isAudioFile(tooBig) ? '25 MB recording' : '10 MB document'} limit`); return; }
    const fd = new FormData(); picked.forEach((f) => fd.append('files', f));
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/api/jurisdiction-requests/drafts/${draftId}/attachments`, fd, auth());
      setFiles(data.attachments); toast.success(`${picked.length} document${picked.length > 1 ? 's' : ''} uploaded`);
    } catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Upload failed — please try again'); }
    finally { setUploading(false); if (fileInput.current) fileInput.current.value = ''; }
  };
  const removeFile = async (fid) => {
    try { await axios.delete(`${API}/api/jurisdiction-requests/drafts/${draftId}/attachments/${fid}`, auth()); setFiles((f) => f.filter((a) => a.file_id !== fid)); }
    catch { toast.error('Could not remove the document'); }
  };
  const openFile = async (a) => {
    try {
      const r = await fetch(`${API}/api/jurisdiction-requests/${draftId}/attachments/${a.file_id}`, auth());
      const url = URL.createObjectURL(await r.blob()); window.open(url, '_blank', 'noopener'); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not open the document'); }
  };
  const downloadFile = async (a) => {
    try {
      const r = await fetch(`${API}/api/jurisdiction-requests/${draftId}/attachments/${a.file_id}`, auth());
      const url = URL.createObjectURL(await r.blob()); const el = document.createElement('a'); el.href = url; el.download = a.filename; el.click(); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not download the document'); }
  };
  const isCounty = COUNTY_TYPES.includes(jType);
  const parent = isCounty ? '' : (parentCountyName || orderData?.property_county || '');

  const setC = (k, v) => setCol((c) => ({ ...c, [k]: PHONE_FIELDS.includes(k) ? formatUsPhone(v) : k === 'make_checks_payable_to' ? v.toUpperCase() : TITLE_FIELDS.includes(k) ? smartTitleCase(v) : v })); // JURDIR-PAYEE-CASE-001: payee UPPERCASE

  const runLookup = async () => {
    const seq = ++dupSeq.current;
    if (!state || name.trim().length < 2) { setDup({ status: 'none', request: null, jurisdiction: null }); return { status: 'none' }; }
    try {
      const { data } = await axios.get(`${API}/api/jurisdiction-requests/lookup`, { params: { state, jurisdiction_type: jType, jurisdiction_name: name.trim(), parent_county_name: parent || undefined }, ...auth() });
      // A rejected request we are revising is not a blocker.
      const res = data.status === 'rejected' && revisionOfId && data.request?.id === revisionOfId ? { status: 'none', request: null, jurisdiction: null } : data;
      if (seq === dupSeq.current) setDup(res);
      return res;
    } catch { return { status: 'none' }; }
  };
  useEffect(() => { const t = setTimeout(runLookup, 400); return () => clearTimeout(t); }, [state, jType, name, parent]); // eslint-disable-line react-hooks/exhaustive-deps
  const blocked = dup.status === 'pending' || dup.status === 'exists' || dup.status === 'approved';

  const errors = {
    state: !state ? 'The order has no property state — set it on the order first.' : '',
    name: name.trim().length < 2 ? 'Enter the jurisdiction / county name (min. 2 characters).' : '',
    parent: !isCounty && !parent ? 'The order has no property county — a parent county is required for this type.' : '',
  };
  const [blurred, setBlurred] = useState({});
  const markBlur = (k) => setBlurred((b) => ({ ...b, [k]: true }));
  const colErrors = Object.fromEntries(COLLECTOR_FIELDS.map((k) => [k, validateCollectorField(k, col[k], { requireMandatory: true })]));
  const showErr = (k) => (touched || blurred[k]) ? colErrors[k] : '';
  const money = (v) => v === '' || v == null || (/^\d+(\.\d{1,2})?$/.test(String(v)) && Number(v) >= 0);
  // PJR-002-B: every workflow field depends on "MLC Required?" — inactive (and never validated) when MLC = No.
  const mlcOn = !!wf.mlc_required;
  const WF_CLEARED = { mlc_charges: '', mlc_24_month_charges: '', mlc_eta_days: '', info_obtained_back: '', sase_required: null };
  const toggleMlc = (v) => setWf((w) => (v ? { ...w, mlc_required: true } : { ...w, mlc_required: false, ...WF_CLEARED }));
  // MLC-VALIDATION-FIX-001 / MLC-FEE-GT-ZERO-001: charges > $0.00, method and SASE are mandatory when MLC Required = Yes.
  const wfErrors = {
    sase_required: mlcOn && wf.sase_required === null ? 'Select Yes or No.' : '',
    mlc_charges: mlcOn && !(String(wf.mlc_charges ?? '').trim() !== '' && money(wf.mlc_charges) && Number(wf.mlc_charges) > 0) ? 'MLC fee must be greater than $0.00.' : '',
    mlc_24_month_charges: mlcOn && !money(wf.mlc_24_month_charges) ? 'Enter a valid amount.' : '',
    mlc_eta_days: mlcOn && wf.mlc_eta_days !== '' && !/^\d{1,3}$/.test(String(wf.mlc_eta_days)) ? 'Whole days only.' : '',
    info_obtained_back: mlcOn && !wf.info_obtained_back ? 'Select Mail, Fax or Email.' : '',
  };
  const hasFiles = files.length > 0;
  const isValid = !Object.values(errors).some(Boolean) && !Object.values(colErrors).some(Boolean) && !Object.values(wfErrors).some(Boolean) && hasFiles;
  const sectionDone = [!errors.name && !errors.parent && !!state, !colErrors.make_checks_payable_to && !colErrors.tax_collector_phone && !!col.make_checks_payable_to && !!col.tax_collector_phone && !!col.tax_collector_physical_address, !Object.values(wfErrors).some(Boolean), hasFiles];
  const Err = ({ k }) => (touched || k === 'state' || k === 'parent') && errors[k] ? <p className="text-[11px] text-rose-600 mt-1" data-testid={`rmj-error-${k}`}>{errors[k]}</p> : null;

  const submitLock = useRef(false);                      // double-click guard (state updates are async)
  const submit = async () => {
    if (submitLock.current || busy) return;
    setTouched(true);
    if (!isValid) { toast.error('Please complete the required fields and fix any invalid entries.'); return; }
    submitLock.current = true;
    setServerError('');
    setBusy(true);
    try {
      const pre = await runLookup();                       // re-check right before posting
      if (pre.status === 'pending' || pre.status === 'exists' || pre.status === 'approved') { setBusy(false); return; }
      const collector = Object.fromEntries(COLLECTOR_FIELDS.map((k) => [k, (PHONE_FIELDS.includes(k) ? digits(col[k]) : col[k].trim()) || null]));
      const { data } = await axios.post(`${API}/api/jurisdiction-requests/from-order`, {
        order_id: orderData.id,
        revision_of_id: revisionOfId || undefined,
        draft_id: draftId,
        payload: { state, jurisdiction_type: jType, jurisdiction_name: name.trim(), parent_county_name: parent || null, ...collector,
          mlc_required: mlcOn, sase_required: mlcOn ? wf.sase_required : null,
          mlc_charges: mlcOn && wf.mlc_charges !== '' ? Number(wf.mlc_charges) : null,
          mlc_24_month_charges: mlcOn && wf.mlc_24_month_charges !== '' ? Number(wf.mlc_24_month_charges) : null,
          mlc_eta_days: mlcOn && wf.mlc_eta_days !== '' ? Number(wf.mlc_eta_days) : null,
          info_obtained_back: mlcOn ? (wf.info_obtained_back || null) : null },
      }, auth());
      setDone(data.request);
      onSubmitted?.(data.request);
    } catch (e) {
      if (e?.response?.status === 409) {                   // concurrent duplicate → show the existing state, not a red error
        const res = await runLookup();
        if (res.status === 'none') setDup({ status: 'pending', request: null, jurisdiction: null });
        setBusy(false);
        return;
      }
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (Array.isArray(d) ? d.map((x) => x.msg).join('; ') : `Failed to submit the request (HTTP ${e?.response?.status || 'network'})`);
      setServerError(msg);
      toast.error(msg);
    } finally { setBusy(false); submitLock.current = false; }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[760px] p-0 gap-0 rounded-[22px] overflow-hidden z-[121] border-0 shadow-2xl shadow-indigo-900/20 duration-200" data-testid="rmj-dialog">
        {!done ? (
          <>
            <DialogHeader className="relative px-7 pt-6 pb-5 text-left overflow-hidden bg-gradient-to-r from-indigo-50 via-white to-violet-50">
              <span aria-hidden className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-gradient-to-br from-indigo-200/60 to-violet-200/40 blur-2xl" />
              <div className="relative flex items-center gap-4">
                <span className="h-14 w-14 rounded-2xl bg-indigo-100/90 text-indigo-600 grid place-items-center shadow-inner shrink-0"><Landmark className="h-7 w-7" /></span>
                <div>
                  <DialogTitle className="text-[22px] font-bold tracking-tight text-slate-900">{revisionOfId ? 'Correct & Resubmit Request' : 'Request Missing Jurisdiction'}</DialogTitle>
                  <DialogDescription className="text-[14px] text-slate-500 mt-0.5">{revisionOfId ? 'Your previous entries are kept — update what was flagged and resubmit for review.' : 'Submit a request to add a new jurisdiction to the directory.'}</DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="px-7 py-3 bg-white border-b border-slate-100 flex items-center justify-between gap-2" data-testid="rmj-stepper">
              {['Jurisdiction Details', 'Tax Collector Details', 'MLC & Workflow', 'Supporting Documents'].map((t, i) => (
                <React.Fragment key={t}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`h-7 w-7 rounded-full grid place-items-center text-[12px] font-bold shrink-0 transition-colors ${sectionDone[i] ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'}`} data-testid={`rmj-step-${i + 1}`}>{i + 1}</span>
                    <span className={`text-[12.5px] font-medium truncate ${sectionDone[i] ? 'text-slate-900' : 'text-slate-500'}`}>{t}</span>
                  </div>
                  {i < 3 && <span className="hidden sm:block h-px flex-1 bg-slate-200 mx-1" />}
                </React.Fragment>
              ))}
            </div>
            <div className="rmj-body bg-slate-50/80 px-6 py-5 space-y-4 max-h-[58vh] overflow-y-auto scroll-smooth">
              {revisionOfId && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-3" data-testid="rmj-revision-banner">
                  <span className="h-8 w-8 rounded-xl bg-rose-100 text-rose-600 grid place-items-center shrink-0"><XCircle className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-rose-900">Correcting rejected request JR-{String(revisionOfId).slice(0, 8).toUpperCase()}</p>
                    <p className="text-[12.5px] text-rose-800 mt-0.5"><span className="font-semibold">Reason:</span> <span data-testid="rmj-revision-reason">{revisionOf?.rejection_reason || 'No reason provided'}</span></p>
                  </div>
                </div>
              )}
              <Section n="1" title="Jurisdiction Details" testId="rmj-section-1">
                <Field k="state" label="State" value={state || '—'} readOnly required />
                <div>
                  <Label required>Jurisdiction / County Name</Label>
                  <Input value={name} onChange={(e) => setName(smartTitleCase(e.target.value))} onBlur={() => setTouched(true)} placeholder="e.g. Castle County" maxLength={120}
                    className={`${INPUT} ${touched && errors.name ? 'border-rose-300' : ''}`} data-testid="rmj-name" />
                  <Err k="name" />
                </div>
                <div>
                  <Label required>Jurisdiction Type</Label>
                  <div className="relative">
                    <select value={jType} onChange={(e) => setJType(e.target.value)} className={`${INPUT} w-full appearance-none border px-3 pr-10 outline-none cursor-pointer`} data-testid="rmj-type">
                      {JURISDICTION_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  </div>
                </div>
                {!isCounty && (
                  <div>
                    <Field k="parent_county" label="Parent County (from order)" value={parent ? `${parent}${/county$/i.test(parent) ? '' : ' County'}` : 'Not set on order'} readOnly required />
                    <Err k="parent" />
                  </div>
                )}
              </Section>

              <Section n="2" title="Tax Collector Details" testId="rmj-section-2">
                <Field k="tax_collector_name" label="Tax Collector Name" value={col.tax_collector_name} onChange={setC} placeholder="e.g. Castle County Treasurer" />
                <Field k="make_checks_payable_to" label="Make Checks Payable To" value={col.make_checks_payable_to} onChange={setC} onBlur={() => markBlur('make_checks_payable_to')} error={showErr('make_checks_payable_to')} placeholder="e.g. Payee printed on checks" required />
                <div className="sm:col-span-2 grid grid-cols-[1fr_96px_1fr] gap-x-4">
                  <Field k="tax_collector_phone" label="Phone" value={col.tax_collector_phone} onChange={setC} onBlur={() => markBlur('tax_collector_phone')} error={showErr('tax_collector_phone')} placeholder="302-291-2654" icon={Phone} inputMode="tel" maxLength={24} required />
                  <Field k="tax_collector_phone_ext" label="Ext" value={col.tax_collector_phone_ext} onChange={setC} placeholder="—" maxLength={10} inputMode="numeric" />
                  <Field k="tax_collector_fax" label="Fax" value={col.tax_collector_fax} onChange={setC} onBlur={() => markBlur('tax_collector_fax')} error={showErr('tax_collector_fax')} placeholder="302-291-2654" icon={Printer} inputMode="tel" maxLength={24} />
                </div>
                <Field k="tax_collector_email" label="Email" value={col.tax_collector_email} onChange={setC} onBlur={() => markBlur('tax_collector_email')} error={showErr('tax_collector_email')} placeholder="collector@county.gov" icon={Mail} className="sm:col-span-2" inputMode="email" />
              </Section>

              <Section n="2" title="Address & Website" testId="rmj-section-address">
                <Field k="tax_collector_working_hours" label="Working Hours" value={col.tax_collector_working_hours} onChange={setC} placeholder="e.g. Mon–Fri 8:30am–4:30pm" icon={Clock3} className="sm:col-span-2" />
                <div>
                  <Label required>Physical Address</Label>
                  <div className="relative">
                    <MapPin className="pointer-events-none absolute left-3.5 top-3.5 h-4 w-4 text-indigo-500" />
                    <Textarea value={col.tax_collector_physical_address} onChange={(e) => setC('tax_collector_physical_address', e.target.value.slice(0, 500))} onBlur={() => markBlur('tax_collector_physical_address')} rows={2} placeholder="Street, City, State ZIP" className={`${INPUT} h-auto min-h-[44px] pl-10 py-2.5 resize-none ${showErr('tax_collector_physical_address') ? 'border-rose-300' : ''}`} data-testid="rmj-tax-collector-physical-address" />
                  </div>
                  {showErr('tax_collector_physical_address') && <p className="text-[11px] text-rose-600 mt-1" data-testid="rmj-error-tax_collector_physical_address">{showErr('tax_collector_physical_address')}</p>}
                </div>
                <div>
                  <Label>Mailing Address</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3.5 top-3.5 h-4 w-4 text-indigo-500" />
                    <Textarea value={col.tax_collector_mailing_address} onChange={(e) => setC('tax_collector_mailing_address', e.target.value.slice(0, 500))} rows={2} placeholder="PO Box or street address" className={`${INPUT} h-auto min-h-[44px] pl-10 py-2.5 resize-none`} data-testid="rmj-tax-collector-mailing-address" />
                  </div>
                </div>
                <Field k="tax_collector_website" label="Tax Collector Website" value={col.tax_collector_website} onChange={setC} onBlur={() => markBlur('tax_collector_website')} error={showErr('tax_collector_website')} placeholder="https://..." icon={Globe} maxLength={300} inputMode="url" />
                <Field k="assessor_website" label="Assessor Website" value={col.assessor_website} onChange={setC} onBlur={() => markBlur('assessor_website')} error={showErr('assessor_website')} placeholder="https://..." icon={Globe} maxLength={300} inputMode="url" />
              </Section>

              <Section n="3" title="MLC & Workflow" testId="rmj-section-3">
                <div className="sm:col-span-2">
                <div className="flex items-center gap-3 rounded-xl bg-indigo-50/70 border border-indigo-100 px-4 py-2.5">
                  <Switch checked={mlcOn} onCheckedChange={(v) => toggleMlc(!!v)} data-testid="rmj-mlc-required" />
                  <div className="min-w-0"><p className="text-[13px] font-semibold text-slate-800">MLC Required?</p><p className="text-[11.5px] text-slate-500" data-testid="rmj-mlc-hint">{mlcOn ? 'Complete the applicable MLC and workflow details below.' : 'MLC and workflow fields are inactive while set to “No”.'}</p></div>
                </div>
                {/* MLC-WORKFLOW-CONDITIONAL-UI-001 — dependent fields are fully hidden while MLC Required = No (validation/clearing logic unchanged) */}
                <MlcCollapse open={mlcOn} testId="rmj-mlc-dependent">
                <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3" data-testid="rmj-mlc-fields">
                  {[['mlc_charges', 'MLC Charges ($)', 'e.g. 50.00', 'decimal'], ['mlc_24_month_charges', '24-Month MLC ($)', 'e.g. 75.00', 'decimal'], ['mlc_eta_days', 'MLC ETA (days)', 'e.g. 5', 'numeric']].map(([k, l, ph, im]) => (
                    <div key={k}>
                      <Label required={k === 'mlc_charges'}>{l}</Label>
                      <Input value={wf[k]} onChange={(e) => setW(k, e.target.value)} placeholder={ph} inputMode={im} maxLength={10}
                        className={`${INPUT} ${touched && wfErrors[k] ? 'border-rose-300' : ''}`} data-testid={`rmj-${k.replace(/_/g, '-')}`} />
                      {touched && wfErrors[k] && <p className="text-[11px] text-rose-600 mt-1">{wfErrors[k]}</p>}
                    </div>
                  ))}
                </div>
                <div>
                  <Label required>Info Obtained Back</Label>
                  <div className="relative">
                    <select value={wf.info_obtained_back} onChange={(e) => setW('info_obtained_back', e.target.value)}
                      className={`${INPUT} w-full appearance-none border px-3 pr-10 outline-none cursor-pointer ${touched && wfErrors.info_obtained_back ? 'border-rose-300' : ''}`} data-testid="rmj-info-obtained-back">
                      <option value="">Select method...</option>
                      {INFO_OBTAINED_BACK_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  </div>
                  {touched && wfErrors.info_obtained_back && <p className="text-[11px] text-rose-600 mt-1" data-testid="rmj-error-info">{wfErrors.info_obtained_back}</p>}
                </div>
                <div>
                  <Label required>SASE Required?</Label>
                  <div className="flex items-center gap-8 h-11" role="radiogroup" data-testid="rmj-sase-required">
                    {[[true, 'Yes'], [false, 'No']].map(([v, l]) => (
                      <label key={l} className="inline-flex items-center gap-2 text-[14px] text-slate-800 cursor-pointer">
                        <input type="radio" name="rmj_sase" checked={wf.sase_required === v} onChange={() => setW('sase_required', v)} className="h-4 w-4 accent-indigo-600" data-testid={`rmj-sase-${l.toLowerCase()}`} />{l}
                      </label>
                    ))}
                  </div>
                  {touched && wfErrors.sase_required && <p className="text-[11px] text-rose-600 mt-1" data-testid="rmj-error-sase">{wfErrors.sase_required}</p>}
                </div>
                </div>
                </MlcCollapse>
                </div>
              </Section>

              <Section n="4" title="Supporting Documents" testId="rmj-section-4">
                <p className="sm:col-span-2 -mt-2 text-[12px] text-slate-500">Attach at least one supporting document or call recording (e.g., tax collector website, official letter, fee schedule, recorded call with the collector).</p>
                <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
                  onClick={() => fileInput.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && fileInput.current?.click()}
                  className={`rounded-2xl border-2 border-dashed px-4 py-6 flex items-center gap-4 cursor-pointer transition-colors ${dragOver ? 'border-indigo-400 bg-indigo-50' : 'border-slate-300 bg-white hover:border-indigo-300'} ${touched && !hasFiles ? 'border-rose-300' : ''}`} data-testid="rmj-dropzone">
                  <span className="h-12 w-12 rounded-xl bg-indigo-50 text-indigo-600 grid place-items-center shrink-0"><UploadCloud className="h-6 w-6" /></span>
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold text-slate-800">{uploading ? 'Uploading…' : <>Drag and drop files here, <span className="text-indigo-600">or click to browse</span></>}</p>
                    <p className="text-[11.5px] text-slate-500">Documents: PDF, JPG, PNG (max 10 MB each) · Call recordings: MP3, WAV, M4A, OGG (max 25 MB each)</p>
                  </div>
                  <input ref={fileInput} type="file" multiple accept="application/pdf,image/jpeg,image/png,.mp3,.wav,.m4a,.ogg,audio/mpeg,audio/wav,audio/mp4,audio/x-m4a,audio/ogg" className="hidden" onChange={(e) => uploadFiles(e.target.files)} data-testid="rmj-file-input" />
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="rmj-uploaded-list">
                  <p className="text-[12.5px] font-semibold text-slate-800 mb-2">Uploaded Documents ({files.length})</p>
                  {files.length === 0 ? <p className="text-[12px] text-slate-400">{touched ? 'At least one document or recording is required.' : 'No documents yet.'}</p> : files.map((a) => {
                    const audio = isAudioAttachment(a);
                    return (
                    <div key={a.file_id} className={`rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 mb-1.5 ${audio ? 'space-y-1.5' : ''}`} data-testid={`rmj-file-${a.file_id}`} data-kind={audio ? 'audio' : 'document'}>
                      <div className="flex items-center gap-3">
                        <span className="h-8 w-8 rounded-lg bg-white border border-slate-200 grid place-items-center shrink-0">{audio ? <FileAudio className="h-4 w-4 text-violet-600" /> : a.content_type === 'application/pdf' ? <FileText className="h-4 w-4 text-rose-500" /> : <ImageIcon className="h-4 w-4 text-indigo-500" />}</span>
                        <div className="min-w-0 flex-1"><p className="text-[12.5px] font-medium text-slate-800 truncate" title={a.filename}>{a.filename}</p><p className="text-[11px] text-slate-400">{(a.size / 1024).toFixed(0)} KB{audio ? ' · Call recording' : ''}</p></div>
                        {!audio && <button type="button" onClick={() => openFile(a)} title="View" className="h-8 w-8 rounded-lg grid place-items-center text-slate-500 hover:bg-white hover:text-indigo-600" data-testid={`rmj-file-view-${a.file_id}`}><Eye className="h-4 w-4" /></button>}
                        <button type="button" onClick={() => downloadFile(a)} title="Download" className="h-8 w-8 rounded-lg grid place-items-center text-slate-500 hover:bg-white hover:text-indigo-600" data-testid={`rmj-file-download-${a.file_id}`}><Download className="h-4 w-4" /></button>
                        <button type="button" onClick={() => removeFile(a.file_id)} title="Remove" className="h-8 w-8 rounded-lg grid place-items-center text-slate-500 hover:bg-white hover:text-rose-600" data-testid={`rmj-file-remove-${a.file_id}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                      {audio && (
                        /* PJR-AUDIO-UPLOAD-001: compact inline player — bearer-fetched blob, no autoplay */
                        <InlineAudioPlayer src={`${API}/api/jurisdiction-requests/${draftId}/attachments/${a.file_id}`} authHeaders={auth().headers} mime={a.content_type} testId={`rmj-audio-${a.file_id}`} />
                      )}
                    </div>
                    );
                  })}
                </div>
              </Section>

              {serverError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-[13px] text-rose-700" data-testid="rmj-server-error">{serverError}</div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200/80 bg-white flex items-center justify-end gap-3">
              {dup.status !== 'none' && (
                <div className={`mr-auto flex items-center gap-2 rounded-xl border px-3 py-2 text-[12px] ${dup.status === 'rejected' ? 'bg-rose-50 border-rose-200 text-rose-800' : dup.status === 'exists' || dup.status === 'approved' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid={`rmj-dup-${dup.status}`}>
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>
                    {dup.status === 'pending' && <>A request for <b>{name.trim()}</b> is already pending{dup.request ? ` (JR-${dup.request.id.slice(0, 8).toUpperCase()}${dup.request.is_mine ? ', yours' : ''})` : ''}.</>}
                    {(dup.status === 'exists' || dup.status === 'approved') && <><b>{name.trim()}</b> is already in the directory{parent ? ` under ${parent}` : ''} — refresh the list and select it.</>}
                    {dup.status === 'rejected' && <>A previous request was rejected{dup.request?.rejection_reason ? `: “${dup.request.rejection_reason}”` : ''}. Submitting files a corrected request.</>}
                  </span>
                  {dup.status === 'pending' && dup.request && onViewRequest && <button type="button" onClick={() => onViewRequest(dup.request)} className="shrink-0 font-semibold underline" data-testid="rmj-dup-view">View request</button>}
                  {(dup.status === 'exists' || dup.status === 'approved') && onRefreshList && <button type="button" onClick={() => { onRefreshList(); onClose(); }} className="shrink-0 font-semibold underline" data-testid="rmj-dup-refresh">Refresh list</button>}
                </div>
              )}
              <Button variant="outline" onClick={onClose} disabled={busy} className="h-11 px-6 rounded-xl border-slate-200 text-[14px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors" data-testid="rmj-cancel">Cancel</Button>
              {!blocked && <Button onClick={submit} disabled={busy || uploading || !isValid} title={!isValid ? (hasFiles ? 'Complete the required fields and fix any invalid entries' : 'Attach at least one supporting document') : ''}
                className="h-11 px-6 rounded-xl text-[14px] font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-md shadow-indigo-500/25 hover:shadow-lg hover:shadow-indigo-500/30 transition-[box-shadow,background-color,transform] duration-150 active:scale-[0.98] disabled:from-slate-300 disabled:to-slate-300 disabled:shadow-none"
                data-testid="rmj-submit">
                <Send className="h-4 w-4 mr-2" /> {busy ? 'Submitting…' : revisionOfId ? 'Resubmit Request' : 'Submit Request'}
              </Button>}
            </div>
          </>
        ) : (
          <div className="p-7" data-testid="rmj-success">
            <div className="flex items-start gap-4">
              <span className="h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-600 grid place-items-center shrink-0"><CheckCircle2 className="h-6 w-6" /></span>
              <div>
                <DialogTitle className="text-[18px] font-bold text-slate-900">Request Submitted Successfully</DialogTitle>
                <DialogDescription className="text-[13px] text-slate-500">Your jurisdiction request has been submitted for review.</DialogDescription>
              </div>
            </div>
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2 text-[13px]">
              {[
                ['Jurisdiction', `${displayTitleCase(done.payload?.jurisdiction_name)}, ${done.payload?.state} · ${getJurisdictionTypeLabel(done.payload?.jurisdiction_type)}`],
                ['Order', done.order_number || done.order_id],
                ['Request ID', <span data-testid="rmj-request-id">JR-{(done.id || '').slice(0, 8).toUpperCase()}</span>],
                ['Status', <span className="text-amber-700">Pending</span>],
              ].map(([l, v]) => <div key={l} className="flex justify-between gap-3"><span className="text-slate-500">{l}</span><span className="font-medium text-slate-800 text-right">{v}</span></div>)}
            </div>
            <p className="mt-3 text-[12px] text-slate-500">You will be notified once the request is reviewed.</p>
            <div className="mt-5 flex justify-end"><Button onClick={onClose} className="h-11 px-6 rounded-xl text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700" data-testid="rmj-close">Close</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
